import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Star, ExternalLink, PenLine } from "lucide-react";
import api from "@/lib/api";

const FALLBACK_SEARCH = "https://www.google.com/search?q=Work+Travel+Expert+LLC+avis";

export const GoogleLogo = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const Stars = ({ value = 0, size = 16 }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} size={size} className={n <= Math.round(value) ? "fill-gold text-gold" : "text-slate-300"} />
    ))}
  </span>
);

export const GoogleReviewsSection = ({ variant = "full", theme = "light", className = "" }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/google-reviews").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data || !data.available) return null;

  const profileUrl = data.profile_url || FALLBACK_SEARCH;
  const writeUrl = data.write_review_url || profileUrl;
  const dark = theme === "dark";
  const reviews = (data.reviews || []).slice(0, variant === "compact" ? 3 : 6);

  /* ---------- Variante compacte (proche du paiement) ---------- */
  if (variant === "compact") {
    return (
      <div data-testid="google-reviews-compact" className={`flex flex-col sm:flex-row sm:items-center gap-4 rounded-2xl border border-gold/30 bg-white px-6 py-5 shadow-sm ${className}`}>
        <div className="flex items-center gap-3">
          <GoogleLogo size={26} />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading text-2xl text-navy leading-none" data-testid="google-compact-rating">{Number(data.rating).toFixed(1)}</span>
              <Stars value={data.rating} size={15} />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{data.count} avis Google · note moyenne vérifiée</p>
          </div>
        </div>
        <p className="text-sm text-slate-700 sm:border-l sm:border-border sm:pl-5 flex-1">
          Des centaines de candidats accompagnés vers l'Australie et le Canada nous font confiance.
        </p>
        <a
          href={profileUrl} target="_blank" rel="noopener noreferrer"
          data-testid="google-compact-link"
          className="inline-flex items-center justify-center gap-2 text-sm font-medium text-navy border border-navy/20 hover:border-gold hover:text-gold px-5 py-2.5 rounded-full transition-colors shrink-0"
        >
          Voir nos avis <ExternalLink size={14} />
        </a>
      </div>
    );
  }

  /* ---------- Variante complète ---------- */
  return (
    <section data-testid="google-reviews-section" className={`relative overflow-hidden ${dark ? "bg-navy" : "bg-white"} ${className}`}>
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-16 sm:py-20">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-xs uppercase tracking-[0.2em] text-gold">
            <GoogleLogo size={15} /> Avis Google vérifiés
          </span>
          <h2 data-testid="google-reviews-title" className={`mt-5 font-heading font-medium text-3xl sm:text-4xl ${dark ? "text-white" : "text-navy"}`}>
            Ce que nos clients disent sur Google
          </h2>
          <div className="gold-line mx-auto mt-6" />

          <div className="mt-8 inline-flex flex-col sm:flex-row items-center gap-4 sm:gap-6 rounded-2xl border border-gold/30 bg-gold/[0.06] px-7 py-5" data-testid="google-reviews-rating">
            <div className="flex items-center gap-3">
              <GoogleLogo size={30} />
              <span className={`font-heading text-4xl leading-none ${dark ? "text-white" : "text-navy"}`}>{Number(data.rating).toFixed(1)}</span>
              <span className={`text-sm ${dark ? "text-white/60" : "text-muted-foreground"}`}>/ 5</span>
            </div>
            <div className="sm:border-l sm:border-gold/25 sm:pl-6 text-center sm:text-left">
              <Stars value={data.rating} size={20} />
              <p className={`mt-1.5 text-sm ${dark ? "text-white/70" : "text-muted-foreground"}`} data-testid="google-reviews-count">
                {data.count} avis Google
              </p>
            </div>
          </div>
        </div>

        {reviews.length > 0 && (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="google-reviews-list">
            {reviews.map((r, i) => (
              <motion.article
                key={`${r.name}-${i}`}
                data-testid={`google-review-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.07 }}
                className={`rounded-2xl border p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold ${dark ? "border-white/10 bg-white/[0.05]" : "border-border bg-[#F8F9FA]"}`}
              >
                <div className="flex items-center gap-3">
                  {r.photo ? (
                    <img src={r.photo} alt="" loading="lazy" className="h-10 w-10 rounded-full object-cover" />
                  ) : (
                    <span className="h-10 w-10 rounded-full bg-navy text-gold flex items-center justify-center font-heading text-lg">
                      {(r.name || "?").charAt(0).toUpperCase()}
                    </span>
                  )}
                  <div className="min-w-0 text-left">
                    <p className={`font-medium truncate ${dark ? "text-white" : "text-navy"}`}>{r.name}</p>
                    <p className={`text-xs ${dark ? "text-white/50" : "text-muted-foreground"}`}>{r.date_label || "Avis Google"}</p>
                  </div>
                  <GoogleLogo size={17} />
                </div>
                <div className="mt-4 text-left"><Stars value={r.rating} size={15} /></div>
                <p className={`mt-3 text-sm leading-relaxed text-left ${dark ? "text-white/85" : "text-slate-700"}`}>“{r.text}”</p>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-11 flex flex-col sm:flex-row justify-center gap-3">
          <a
            href={profileUrl} target="_blank" rel="noopener noreferrer"
            data-testid="google-reviews-all-btn"
            className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-7 py-3.5 font-medium rounded-full transition-colors"
          >
            <GoogleLogo size={17} /> Voir tous nos avis sur Google <ExternalLink size={15} />
          </a>
          <a
            href={writeUrl} target="_blank" rel="noopener noreferrer"
            data-testid="google-reviews-write-btn"
            className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 font-medium rounded-full border transition-colors ${dark ? "border-white/25 text-white hover:border-gold hover:text-gold" : "border-navy/20 text-navy hover:border-gold hover:text-gold"}`}
          >
            <PenLine size={16} /> Laisser un avis sur Google
          </a>
        </div>

        <p className={`mt-6 text-center text-xs ${dark ? "text-white/45" : "text-muted-foreground"}`}>
          Avis issus de notre fiche Google · classés par pertinence · Google et le logo Google sont des marques de Google LLC.
        </p>
      </div>
    </section>
  );
};
